<template>
  <div v-if="orgData" class="the-navbar__user-meta flex items-center">
    <vs-dropdown
      vs-trigger-click
      class="cursor-pointer dropdown-container"
      ref="orgDropdown"
    >
      <vx-tooltip text="Click to switch organization" position="bottom">
        <div
          ref="dropdownButton"
          class="dropdown-button flex items-center px-3 py-2 rounded-lg cursor-pointer bg-white hover:bg-gray-50 border shadow-sm transition-all duration-200">
          <div v-if="orgData.name" class="org-name text-left leading-tight hidden sm:block items-center mr-3">
            <h6 class="font-semibold">{{ orgData.name }}</h6>
            <span class="text-xs text-gray-500">{{ $t('orgDashboard.main.currentOrg') }}</span>
          </div>

          <div class="con-img relative">
            <div class="org-status-indicator" :class="{ 'active': true }"></div>
            <vs-avatar v-if="orgData.logo" class="m-0 border-2 border-white shadow-sm" size="36px"
              :src="constants.s3Bucket + '/organization/logo/' + orgData.logo" />
            <vs-avatar v-else class="m-0 border-2 border-white shadow-sm" size="36px"
              :src="require('@/assets/images/icons/organization/business_black_36dp.svg')" />
          </div>

          <feather-icon icon="ChevronDownIcon" svgClasses="h-5 w-5 ml-2 text-gray-500" />
        </div>
      </vx-tooltip>

      <vs-dropdown-menu class="vx-navbar-dropdown">
        <ul style="min-width: 15rem;">
          <!-- Organization Section Header -->
          <li class="py-3 px-4 bg-gray-50 border-b">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <feather-icon icon="BriefcaseIcon" svgClasses="w-4 h-4 mr-2 text-primary" />
                <span class="font-medium text-gray-800">{{ $t('orgDashboard.main.myOrganizations') }}</span>
              </div>
              <span v-if="totalOrganizations > 0" class="text-xs bg-primary text-white px-2 py-1 rounded-full">{{
                totalOrganizations }}</span>
            </div>
          </li>

          <!-- Scrollable Organizations List -->
          <div class="organizations-list" style="max-height: 200px; overflow-y: auto;">
            <template v-if="myOrganizations.superOrgs.length > 0 || myOrganizations.subOrgs.length > 0">
              <!-- Super Organizations -->
              <template v-for="(myOrgs, index) in myOrganizations.superOrgs">
                <li v-if="myOrgs && myOrgs.status !== 'DEACTIVATED'" :key="'superOrg-' + index"
                  class="py-2 px-4 cursor-pointer hover:bg-primary hover:text-white pl-10"
                  @click="manageOrganization(myOrgs.slug || myOrgs._id)">
                  <div class="flex items-center">
                    <vs-avatar v-if="myOrgs.logo" class="mr-2" size="24px"
                      :src="constants.s3Bucket + '/organization/logo/' + myOrgs.logo" />
                    <vs-avatar v-else class="mr-2" size="24px"
                      :src="require('@/assets/images/icons/organization/business_black_36dp.svg')" />
                    <span>{{ myOrgs.name }}</span>
                    <vs-icon v-if="isCurrentOrg(myOrgs.slug || myOrgs._id)" icon="check" class="ml-auto text-primary" />
                  </div>
                </li>
              </template>

              <!-- Sub Organizations -->
              <template v-for="(myOrgs, index) in myOrganizations.subOrgs">
                <li
                  v-if="myOrgs && myOrgs.status !== 'DEACTIVATED' && myOrgs.organization && myOrgs.organization.length > 0"
                  :key="'subOrg-' + index" class="py-2 px-4 cursor-pointer hover:bg-primary hover:text-white pl-10"
                  @click="myOrgs.status === 'active' ? manageOrganization(myOrgs.organization[0].slug || myOrgs.organization[0]._id) : acceptAdminOrgInvite(myOrgs._id)">
                  <div class="flex items-center">
                    <vs-avatar v-if="myOrgs.organization[0] && myOrgs.organization[0].logo" class="mr-2" size="24px"
                      :src="constants.s3Bucket + '/organization/logo/' + myOrgs.organization[0].logo" />
                    <vs-avatar v-else class="mr-2" size="24px"
                      :src="require('@/assets/images/icons/organization/business_black_36dp.svg')" />
                    <span>{{ myOrgs.organization[0].name }}</span>
                    <span v-if="myOrgs.status !== 'active'" class="ml-auto text-warning text-xs">{{
                      $t('orgDashboard.main.pendingInvitation') }}</span>
                    <vs-icon v-else-if="isCurrentOrg(myOrgs.organization[0].slug || myOrgs.organization[0]._id)"
                      icon="check" class="ml-auto text-primary" />
                  </div>
                </li>
              </template>
            </template>
            <li v-else class="py-2 px-4 text-center pl-10">
              {{ $t('orgDashboard.main.noOrganizationFound') }}
            </li>
          </div>

          <!-- Organization Actions (Not Scrollable) -->
          <li class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white pl-10"
            @click="$router.push('/organization/create/company-profile').catch(() => { })">
            <feather-icon icon="PlusCircleIcon" svgClasses="w-4 h-4" />
            <span class="ml-2">{{ $t('orgDashboard.main.createOrganization') }}</span>
          </li>

          <vs-divider class="m-1" />

          <!-- Language Switcher -->
          <li class="py-2 px-4">
            <div class="flex items-center">
              <feather-icon icon="GlobeIcon" svgClasses="w-4 h-4 mr-4" />
              <div class="flex border rounded overflow-hidden">
                <div class="px-3 py-1 text-xs font-medium cursor-pointer transition-colors flex items-center"
                  :class="$i18n.locale === 'en' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'"
                  @click="updateLocale('en')">
                  <img class="h-3 w-4 mr-1" src="@/assets/images/flags/en.png" alt="en" />
                  EN
                </div>
                <div class="px-3 py-1 text-xs font-medium cursor-pointer transition-colors flex items-center"
                  :class="$i18n.locale === 'pt' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'"
                  @click="updateLocale('pt')">
                  <img class="h-3 w-4 mr-1" src="@/assets/images/flags/pt.png" alt="pt" />
                  PT
                </div>
              </div>
            </div>
          </li>

          <vs-divider class="m-1" />

          <!-- Profile Options -->
          <li class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white"
            @click="$router.push({ name: 'org-settings' }).catch(() => { })">
            <feather-icon icon="SettingsIcon" svgClasses="w-4 h-4" />
            <span class="ml-2">Settings</span>
          </li>

          <li class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white" @click="logout">
            <feather-icon icon="LogOutIcon" svgClasses="w-4 h-4" />
            <span class="ml-2">Logout</span>
          </li>
        </ul>
      </vs-dropdown-menu>
    </vs-dropdown>
  </div>
</template>

<script>
import constants from "@/constants";

export default {
  name: "CombinedDropDown",
  data() {
    return {
      constants,
      dropdownOpen: false
    }
  },
  computed: {
    userData() {
      return this.$store.state.users.data;
    },
    orgData() {
      return this.$store.state.organization.orgData;
    },
    currentLanguage() {
      return this.$i18n.locale === 'en' ? 'EN' : 'PT';
    },
    myOrganizations() {
      const orgs = this.$store.state.organization.myOrganizations;
      if (!orgs) return { superOrgs: [], subOrgs: [] };

      return {
        superOrgs: Array.isArray(orgs.superOrgs) ? orgs.superOrgs : [],
        subOrgs: Array.isArray(orgs.subOrgs) ? orgs.subOrgs : []
      };
    },
    currentOrgId() {
      const orgData = this.$store.state.organization.orgData;
      return orgData ? (orgData.slug || orgData._id) : null;
    },
    totalOrganizations() {
      if (!this.myOrganizations) return 0;

      const activeSuper = Array.isArray(this.myOrganizations.superOrgs)
        ? this.myOrganizations.superOrgs.filter(org => org && org.status !== 'DEACTIVATED').length
        : 0;

      const activeSub = Array.isArray(this.myOrganizations.subOrgs)
        ? this.myOrganizations.subOrgs.filter(org =>
          org && org.status !== 'DEACTIVATED' && org.organization && Array.isArray(org.organization) && org.organization.length > 0
        ).length
        : 0;

      return activeSuper + activeSub;
    }
  },
  mounted() {
    // Close dropdown on scroll
    window.addEventListener('scroll', this.closeDropdownOnScroll, true);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.closeDropdownOnScroll, true);
  },
  methods: {
    closeDropdownOnScroll() {
      // Close the dropdown when scrolling
      if (this.$refs.orgDropdown && this.$refs.orgDropdown.$el) {
        const dropdown = this.$refs.orgDropdown.$el.querySelector('.vs-dropdown--menu');
        if (dropdown && dropdown.classList.contains('vs-dropdown--menu-active')) {
          // Trigger close by clicking outside or programmatically
          this.$refs.orgDropdown.$el.click();
        }
      }
    },
    isCurrentOrg(orgId) {
      return this.currentOrgId === orgId;
    },
    updateLocale(locale) {
      this.$i18n.locale = locale;
      localStorage.setItem('locale', locale);
    },
    logout() {
      this.$store.dispatch("alex/deleteSession", this.$store.state.auth.user._id);
      this.$store.dispatch("boards/deleteSession", this.$store.state.auth.user._id);
      this.$store.dispatch("editor/deleteSession", this.$store.state.auth.user._id);
      this.$store.dispatch("auth/logout").then(() => {
        window.location.href = "/";
      });
    },
    acceptAdminOrgInvite(adminId) {
      this.$store
        .dispatch("organization/acceptAdminOrgInvite", { adminId })
        .then((res) => {
          window.location.href = "/my-organizations";
        })
        .catch((err) => {
          console.error("Error accepting admin invitation:", err);
        });
    },
    manageOrganization(organizationIdORSlug) {
      if (!organizationIdORSlug) {
        console.error("No organization ID or slug provided");
        return;
      }

      if (this.$store.state.auth && this.$store.state.auth.user) {
        const userId = this.$store.state.auth.user._id;
        const lastOrgKey = `lastOrg_${userId}`;
        localStorage.setItem(lastOrgKey, organizationIdORSlug);
      }

      window.location.href = `/organization/${organizationIdORSlug}/dashboard`;
    },
  },
  created() {
    try {
      if (this.$store.state.auth && this.$store.state.auth.user) {
        const payload = {
          email: this.$store.state.auth.user.email,
          userId: this.$store.state.auth.user._id,
        };
        this.$store
          .dispatch("organization/fetchAllMyOrganizations", payload)
          .catch((err) => {
            console.error("Error fetching organizations:", err);
          });
      }
    } catch (error) {
      console.error("Error in CombinedDropDown created hook:", error);
    }
  },
};
</script>

<style scoped>
.org-dropdown {
  margin-right: 1rem;
}

.organizations-list {
  border-radius: 4px;
  background-color: #fff;
}

.organizations-list::-webkit-scrollbar {
  width: 5px;
}

.organizations-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.organizations-list::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 10px;
}

.organizations-list::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}

.dropdown-toggle {
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 4px;
}

.dropdown-toggle:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Organization status indicator */
/* .org-status-indicator {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #ddd;
  border: 2px solid white;
  bottom: -1px;
  right: -1px;
  z-index: 1;
} */

/* .org-status-indicator.active {
  background-color: #60BF19;
} */

/* Organization name styling */
.org-name h6 {
  line-height: 1.2;
}

/* Enhanced dropdown toggle */
.dropdown-button {
  position: relative;
  border-radius: 6px;
  transition: all 0.2s ease;
  background-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #eaeaea;
}

.dropdown-button:hover {
  background-color: rgba(251, 191, 21, 0.1);
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.dropdown-button:after {
  content: "";
  position: absolute;
  bottom: -3px;
  left: 50%;
  transform: translateX(-50%);
  width: 40%;
  height: 2px;
  background-color: var(--vs-primary, #FBBF15);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.dropdown-button:hover:after {
  opacity: 1;
}

.org-name {
  text-align: left !important;
}

.organizations-list li span:first-of-type {
  display: block;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

</style>